import { useEffect } from "react";
import { useThemeMode } from "@contexts/ThemeContext";
import { createDebugLogger } from "@utils/debugLog";
import { isTeamsEmbed } from "./embedHost";
import {
  ensureTeamsSdkReady,
  getTeamsTheme,
  mapTeamsThemeToThemeMode,
  notifyTeamsTabFailed,
  notifyTeamsTabLoaded,
  onTeamsThemeChange,
} from "./teamsHost";

const log = createDebugLogger("embed:teams-tab");

/**
 * Runs the Teams tab lifecycle: initialises the SDK, adopts the Teams client
 * theme and keeps following it, then tells Teams the tab is ready.
 *
 * A no-op anywhere but a Teams tab. In particular it does not run in the
 * sign-in popup, which Teams opens on a URL without the embed parameter — the
 * popup reports through `authentication.notifySuccess`, not this one.
 */
export const useTeamsTab = (): void => {
  const { setThemeMode } = useThemeMode();

  useEffect(() => {
    if (!isTeamsEmbed()) {
      return;
    }

    let cancelled = false;

    const start = async (): Promise<void> => {
      try {
        await ensureTeamsSdkReady();
      } catch (error) {
        // Nothing can be reported to Teams when the SDK never came up, and the
        // app itself works regardless — it just will not follow the theme.
        log("Teams SDK did not initialise", error);
        return;
      }

      if (cancelled) {
        return;
      }

      try {
        const teamsTheme = await getTeamsTheme();
        if (cancelled) {
          return;
        }

        if (teamsTheme) {
          setThemeMode(mapTeamsThemeToThemeMode(teamsTheme));
        }

        onTeamsThemeChange((themeMode) => {
          setThemeMode(themeMode);
        });

        await notifyTeamsTabLoaded();
      } catch (error) {
        log("Teams tab startup failed", error);
        notifyTeamsTabFailed(
          error instanceof Error ? error.message : String(error),
        );
      }
    };

    void start();

    return () => {
      cancelled = true;
    };
  }, [setThemeMode]);
};
